import type { InputHTMLAttributes, PropsWithChildren, ReactNode } from "react";
import type { Control, FieldValues, Path } from "react-hook-form";

import { cn } from "@krea/shared/utils";

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField,
} from "../Form";
import { Input } from "../inputs/Input";

interface StringInputProps<T extends FieldValues>
  extends Pick<
    InputHTMLAttributes<HTMLInputElement>,
    "className" | "placeholder"
  > {
  name: Path<T>;
  label?: string;
  control: Control<T>;
  required?: boolean;
  rightElement?: ReactNode;
  leftElement?: ReactNode;
  onChangeHandler?: (value: string) => void;
}

function StringInputControl({
  value,
  onChange,
  restField,
  rest,
  isSubmitting,
  leftElement,
  rightElement,
  className,
  onChangeHandler,
  children,
}: {
  value: string | undefined;
  onChange: (v: string) => void;
  restField: Record<string, unknown>;
  rest: Record<string, unknown>;
  isSubmitting: boolean;
  leftElement?: ReactNode;
  rightElement?: ReactNode;
  className?: string;
  onChangeHandler?: (value: string) => void;
  children?: ReactNode;
}) {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call -- useFormField is typed in Form.tsx
  const { formItemId } = useFormField();

  return (
    <div className="flex w-full flex-col">
      <div className="relative flex w-full">
        {leftElement && (
          <div className="absolute left-0 top-0 z-10 flex h-full items-center pl-3">
            {leftElement}
          </div>
        )}
        {rightElement ? (
          <>
            <Input
              {...rest}
              {...restField}
              className={cn(
                "flex-1 min-w-0 rounded-r-none",
                leftElement && "pl-10",
                className,
              )}
              value={value ?? ""}
              id={formItemId}
              type="text"
              onChange={(e) => {
                onChange(e.target.value);
                onChangeHandler?.(e.target.value);
              }}
              disabled={isSubmitting}
            />
            <div
              className={cn(
                "pointer-events-none flex h-10 shrink-0 items-center justify-center rounded-r-md border border-gray-200 border-l-0 bg-gray-50 px-3 text-sm font-semibold text-black",
                isSubmitting && "opacity-50",
              )}
            >
              {rightElement}
            </div>
          </>
        ) : (
          <Input
            {...rest}
            {...restField}
            className={cn(leftElement && "pl-10", className)}
            value={value ?? ""}
            id={formItemId}
            type="text"
            onChange={(e) => {
              onChange(e.target.value);
              onChangeHandler?.(e.target.value);
            }}
            disabled={isSubmitting}
          />
        )}
        {children}
      </div>
      <FormMessage />
    </div>
  );
}

export const ControlledStringInput = <T extends FieldValues>({
  name,
  control,
  required = true,
  leftElement,
  rightElement,
  label,
  className,
  onChangeHandler,
  children,
  ...rest
}: PropsWithChildren<StringInputProps<T>>) => {
  return (
    <FormField
      control={control}
      name={name}
      render={({
        field: { value, onChange, ...restField },
        formState: { isSubmitting },
      }) => (
        <FormItem>
          {label && (
            <FormLabel className="font-bold">
              {label} {required && <span className="text-red-300">*</span>}
            </FormLabel>
          )}
          <FormControl>
            <StringInputControl
              value={value}
              onChange={onChange}
              restField={restField}
              rest={rest}
              isSubmitting={isSubmitting}
              leftElement={leftElement}
              rightElement={rightElement}
              className={className}
              onChangeHandler={onChangeHandler}
            >
              {children}
            </StringInputControl>
          </FormControl>
        </FormItem>
      )}
    />
  );
};
